import { useEffect, useMemo, useRef, useState } from "react";
import { buildBoardFromAssignments } from "../boardBuild.js";
import { HexBoard } from "../components/HexBoard.js";
import { PlayerPanel } from "../components/PlayerPanel.js";
import { EventLog } from "../components/EventLog.js";
import {
  PlaybackControls,
  SPEED_PRESETS,
  type JumpKind,
} from "../components/PlaybackControls.js";
import { ReplayEngine } from "../stateReplayer.js";
import type { Replay, ReplayEvent } from "../types.js";

export interface ReplayScreenProps {
  replay: Replay;
  onExit: () => void;
}

export interface JumpTargets {
  combatIdx: number;
  compassIdx: number;
  landmarkIdx: number;
  treasureDug: number;
  finalEscape: number;
}

export function computeJumpTargets(engine: ReplayEngine): JumpTargets {
  return {
    combatIdx: engine.findEventIndex((e) => e.type === "SWORD_USED"),
    compassIdx: engine.findEventIndex((e) => e.type === "COMPASS_USED"),
    landmarkIdx: engine.findEventIndex(
      (e) => e.type === "MAP_LANDMARK_PLACED",
    ),
    treasureDug: engine.findEventIndex((e) => e.type === "TREASURE_DUG"),
    finalEscape: engine.findEventIndex((e) => e.type === "GAME_WON"),
  };
}

export function jumpAvailabilityAt(
  engine: Pick<ReplayEngine, "findEventIndex">,
  targets: JumpTargets,
  index: number,
): Record<JumpKind, boolean> {
  return {
    first_combat: targets.combatIdx !== -1,
    first_compass: targets.compassIdx !== -1,
    first_landmark: targets.landmarkIdx !== -1,
    treasure_dug: targets.treasureDug !== -1,
    next_handoff:
      engine.findEventIndex((e: ReplayEvent) => e.type === "TREASURE_STOLEN", index + 1) !==
      -1,
    final_escape: targets.finalEscape !== -1,
  };
}

export function ReplayScreen({ replay, onExit }: ReplayScreenProps) {
  const board = useMemo(
    () => buildBoardFromAssignments(replay.envelope.initial_setup.board_assignments),
    [replay],
  );
  const engine = useMemo(
    () => new ReplayEngine(replay.envelope, replay.events),
    [replay],
  );

  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speedLabel, setSpeedLabel] = useState("1×");
  const [showHidden, setShowHidden] = useState(
    replay.envelope.spectator_events_included,
  );
  const [showCoords, setShowCoords] = useState(false);

  const state = useMemo(() => engine.stateAt(index), [engine, index]);

  useEffect(() => {
    if (!playing) return;
    const preset = SPEED_PRESETS.find((s) => s.label === speedLabel);
    const ms = preset?.msPerEvent ?? 400;
    if (ms === "instant") {
      setIndex(engine.eventCount());
      setPlaying(false);
      return;
    }
    if (index >= engine.eventCount()) {
      setPlaying(false);
      return;
    }
    const t = setTimeout(() => setIndex((i) => Math.min(i + 1, engine.eventCount())), ms);
    return () => clearTimeout(t);
  }, [playing, index, engine, speedLabel]);

  const turnBoundaries = useMemo(() => engine.turnBoundaries(), [engine]);
  const prevTurn = () => {
    let target = 0;
    for (const b of turnBoundaries) {
      if (b < index) target = b;
      else break;
    }
    setIndex(target);
  };
  const nextTurn = () => {
    for (const b of turnBoundaries) {
      if (b > index) {
        setIndex(b);
        return;
      }
    }
    setIndex(engine.eventCount());
  };

  const jumpTargets = useMemo(() => computeJumpTargets(engine), [engine]);

  const nextHandoff = () => {
    const i = engine.findEventIndex(
      (e) => e.type === "TREASURE_STOLEN",
      index + 1,
    );
    if (i !== -1) setIndex(i + 1);
  };

  const jump = (kind: JumpKind) => {
    const idxMap: Record<JumpKind, number> = {
      first_combat: jumpTargets.combatIdx,
      first_compass: jumpTargets.compassIdx,
      first_landmark: jumpTargets.landmarkIdx,
      treasure_dug: jumpTargets.treasureDug,
      next_handoff: -1,
      final_escape: jumpTargets.finalEscape,
    };
    if (kind === "next_handoff") {
      nextHandoff();
      return;
    }
    const i = idxMap[kind];
    if (i !== -1) setIndex(i + 1);
  };

  const jumpAvailability = jumpAvailabilityAt(engine, jumpTargets, index);
  const warnings = [...replay.warnings, ...state.warnings];

  return (
    <div className="replay">
      <header className="replay__header">
        <button onClick={onExit}>← Library</button>
        <div>
          <strong>{replay.envelope.game_id}</strong> · seed{" "}
          {replay.envelope.seed} · winner{" "}
          {replay.envelope.result.winnerId ?? "—"} · {replay.envelope.result.status}
        </div>
        {!replay.envelope.spectator_events_included && (
          <span className="replay__badge">
            Replay was recorded without spectator events; hidden-info toggle is
            informational only.
          </span>
        )}
        {warnings.length > 0 && (
          <span className="replay__warning" title={warnings.join("\n")}>
            {warnings.length} warning{warnings.length === 1 ? "" : "s"}
          </span>
        )}
      </header>
      <div className="replay__grid">
        <aside className="replay__panels">
          {state.players.map((p) => (
            <PlayerPanel
              key={p.id}
              player={p}
              showHidden={showHidden}
              active={state.active_player_id === p.id}
              winner={state.winner_id === p.id}
            />
          ))}
        </aside>
        <main className="replay__board">
          <HexBoard board={board} state={state} showCoords={showCoords} />
        </main>
        <aside className="replay__log">
          <EventLog
            lines={state.event_log}
            focusIndex={state.event_log.length - 1}
          />
        </aside>
      </div>
      <PlaybackControls
        index={index}
        count={engine.eventCount()}
        playing={playing}
        speedLabel={speedLabel}
        showHidden={showHidden}
        showCoords={showCoords}
        onIndexChange={setIndex}
        onPrevEvent={() => setIndex((i) => Math.max(0, i - 1))}
        onNextEvent={() =>
          setIndex((i) => Math.min(engine.eventCount(), i + 1))
        }
        onPrevTurn={prevTurn}
        onNextTurn={nextTurn}
        onPlayPause={() => setPlaying((p) => !p)}
        onSpeed={setSpeedLabel}
        onToggleHidden={() => setShowHidden((v) => !v)}
        onToggleCoords={() => setShowCoords((v) => !v)}
        onJump={jump}
        jumpAvailability={jumpAvailability}
        currentEvent={
          index > 0 ? engine.eventAt(index - 1) : undefined
        }
      />
    </div>
  );
}
